import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Layout } from "../../components/layout/Layout";
import { PageHeader } from "../../components/layout/PageHeader";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/Card";
import { Button } from "../../components/ui/Button";
import { BookOpen, Star, Rocket, MessageCircle } from "lucide-react";
import { AssignmentProgressBubbly } from "../../components/student/AssignmentProgressBubbly";
import { TutorAnalyticsDashboard } from "../../components/student/TutorAnalyticsDashboard";
import { CommunicationCanDoProgressCard } from "../../components/student/CommunicationCanDoProgressCard";
import { TutorLessonProgressPanel } from "../../components/student/TutorLessonProgressPanel";
import { listAssignments, listSubmissions } from "../../modules/learning/api/assignments";
import { COMMUNICATION_SKILLS_LESSON_NO, getCanDosWithTopics } from "../../services/api/canDo";
import { getCommunicationBadgesMe } from "../../services/api/communicationLearning";
import { getStudentMe, resolveStudentGrade, type RosterStudent } from "../../modules/roster/api/roster.client";
import { practiceTopicsForCommunicationTutor } from "../../utils/canDoTopic";
import { ROUTES } from "../../utils/constants";
import { CommunicationAssignmentsPanel } from "./CommunicationAssignmentsPanel";

export const StudentDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [assignmentStats, setAssignmentStats] = useState({
    total: 0,
    submitted: 0,
    reviewed: 0,
    recentFeedback: [] as Array<{ title: string; grade: number; feedback?: string }>,
  });
  const [communicationPracticeItems, setCommunicationPracticeItems] = useState<
    { id: string; title: string; description?: string }[]
  >([]);
  const [topicsLoading, setTopicsLoading] = useState(false);
  const [commBadges, setCommBadges] = useState<{ badge_key?: string }[]>([]);

  const loadAssignmentStats = useCallback(async () => {
    try {
      let grade: string | undefined;
      try {
        const me = await getStudentMe();
        grade = me.grade ?? undefined;
      } catch {
        // ignore
      }
      const [assignRes, subRes] = await Promise.all([
        listAssignments({ grade, limit: 100 }),
        listSubmissions({ limit: 100 }),
      ]);
      const subByAsn = new Map(subRes.items.map((s) => [s.assignment_id, s]));
      let submitted = 0;
      let reviewed = 0;
      const recentFeedback: Array<{ title: string; grade: number; feedback?: string }> = [];
      for (const a of assignRes.items) {
        const sub = subByAsn.get(a.assignment_id);
        if (sub) {
          submitted++;
          if (sub.status === "published" || sub.status === "reviewed") {
            reviewed++;
            if (sub.grade != null && recentFeedback.length < 3) {
              recentFeedback.push({
                title: a.title,
                grade: sub.grade,
                feedback: sub.feedback ?? undefined,
              });
            }
          }
        }
      }
      setAssignmentStats({
        total: assignRes.items.length,
        submitted,
        reviewed,
        recentFeedback,
      });
    } catch {
      setAssignmentStats({ total: 0, submitted: 0, reviewed: 0, recentFeedback: [] });
    }
  }, []);

  const loadTopics = useCallback(async () => {
    setTopicsLoading(true);
    try {
      const me = await getStudentMe();
      const grade = resolveStudentGrade(me as RosterStudent);
      if (grade == null) {
        setCommunicationPracticeItems([]);
        return;
      }
      const { canDos, communicationTopics } = await getCanDosWithTopics(
        grade,
        COMMUNICATION_SKILLS_LESSON_NO
      );
      const rows = practiceTopicsForCommunicationTutor({
        communication_topics: communicationTopics,
        can_dos: canDos,
      });
      setCommunicationPracticeItems(
        rows.slice(0, 20).map((r) => ({
          id: r.id,
          title: r.title,
          description: r.description,
        }))
      );
    } catch {
      setCommunicationPracticeItems([]);
    } finally {
      setTopicsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAssignmentStats();
  }, [loadAssignmentStats]);

  useEffect(() => {
    loadTopics();
  }, [loadTopics]);

  useEffect(() => {
    getCommunicationBadgesMe()
      .then((b) => setCommBadges((b.items as { badge_key?: string }[]) ?? []))
      .catch(() => setCommBadges([]));
  }, []);
  // ✅ Keep same "summary cards" functionality like existing code
  const lessonsCount = 12;
  const badgesCount = 28;
  const progressPct = 87;

  // ✅ public/images/...
  const IMAGES = {
    card1: "/images/kurinji.jpeg",
    card2: "/images/marudham.jpeg",
    card3: "/images/neidhalpaalai.jpeg",
  };

  // ✅ Stronger scrim for better text contrast
  const scrim = "absolute inset-0 bg-gradient-to-b from-black/60 via-black/30 to-black/60";

  // ✅ Enhanced text shadows for better readability
  const titleClass = "text-white text-2xl font-extrabold drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]";
  const numberClass = "text-white text-5xl font-black drop-shadow-[0_4px_8px_rgba(0,0,0,0.9)]";
  const pillClass = "text-white text-xl font-semibold bg-black/40 px-4 py-2 rounded-full inline-block border-2 border-white/40 drop-shadow-[0_3px_6px_rgba(0,0,0,0.7)]";

  // ✅ Enhanced chip styling with backdrop blur
  const chipClass = "bg-black/30 px-4 py-2 rounded-2xl border-2 border-white/30 drop-shadow-[0_3px_6px_rgba(0,0,0,0.6)] backdrop-blur-sm";
  const iconBoxClass = "p-3 bg-black/30 rounded-2xl ring-2 ring-white/40 drop-shadow-[0_3px_6px_rgba(0,0,0,0.6)] backdrop-blur-sm";

  return (
    <Layout>
      {/* <PageHeader
        title="🎉 Welcome Super Star!"
        subtitle="Let's have fun learning today! ✨"
      /> */}

      {/* <div className="grid grid-cols-1 md:grid-cols-3 gap-8 p-4">
        <Card className="relative overflow-hidden text-white shadow-2xl hover:scale-[1.02] transform transition-all duration-300 border-4 border-white/30 rounded-3xl">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url("${IMAGES.card1}")` }}
          />
          <div className={scrim} />

          <div className="relative z-10">
            <CardHeader className="pb-4">
              <div className="flex items-center gap-4">
                <div className={iconBoxClass}>
                  <BookOpen className="w-10 h-10 text-yellow-300 drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]" />
                </div>
                <div className={chipClass}>
                  <CardTitle className={titleClass}>Magic Lessons</CardTitle>
                </div>
              </div>
            </CardHeader>

            <CardContent className="pt-0">
              <p className={numberClass}>{lessonsCount}</p>
              <p className={pillClass}>Fun Adventures Waiting! 🚀</p>
            </CardContent>
          </div>
        </Card>

        <Card className="relative overflow-hidden text-white shadow-2xl hover:scale-[1.02] transform transition-all duration-300 border-4 border-white/30 rounded-3xl">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url("${IMAGES.card2}")` }}
          />
          <div className={scrim} />

          <div className="relative z-10">
            <CardHeader className="pb-4">
              <div className="flex items-center gap-4">
                <div className={iconBoxClass}>
                  <Star className="w-10 h-10 text-yellow-400 drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]" />
                </div>
                <div className={chipClass}>
                  <CardTitle className={titleClass}>Super Stars ⭐</CardTitle>
                </div>
              </div>
            </CardHeader>

            <CardContent className="pt-0">
              <p className={numberClass}>{badgesCount}</p>
              <p className={pillClass}>Shiny Badges Won! 🏆</p>
            </CardContent>
          </div>
        </Card>

        <Card className="relative overflow-hidden text-white shadow-2xl hover:scale-[1.02] transform transition-all duration-300 border-4 border-white/30 rounded-3xl">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url("${IMAGES.card3}")` }}
          />
          <div className={scrim} />

          <div className="relative z-10">
            <CardHeader className="pb-4">
              <div className="flex items-center gap-4">
                <div className={iconBoxClass}>
                  <Rocket className="w-10 h-10 text-orange-400 drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]" />
                </div>
                <div className={chipClass}>
                  <CardTitle className={titleClass}>Super Progress</CardTitle>
                </div>
              </div>
            </CardHeader>

            <CardContent className="pt-0">
              <p className={numberClass}>{progressPct}%</p>
              <p className={pillClass}>You're Almost a Hero! 💥</p>
            </CardContent>
          </div>
        </Card>
      </div> */}

      {/* Today's learning + AI tutor lesson progress */}
      <TutorLessonProgressPanel />

      {/* Assignment progress — bubbly stats + glass assignment cards */}
      {assignmentStats.total > 0 && (
        <AssignmentProgressBubbly
          stats={assignmentStats}
          onViewAll={() => navigate(ROUTES.STUDENT_ASSIGNMENTS)}
        />
      )}

      {/* 4. Communication Practice Section */}
      {communicationPracticeItems.length > 0 && (
        <Card className="mt-8">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg">
                  <MessageCircle className="w-6 h-6 text-indigo-700 dark:text-indigo-300" />
                </div>
                <CardTitle>Communication Practice</CardTitle>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(ROUTES.STUDENT_COMMUNICATION_TUTOR)}
              >
                View All
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
              Practice Tamil conversation with the Communication Tutor using your grade Can-Do goals.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {communicationPracticeItems.map((topic) => (
                <button
                  key={topic.id}
                  type="button"
                  onClick={() =>
                    navigate(ROUTES.STUDENT_COMMUNICATION_TUTOR + "?topic_id=" + encodeURIComponent(topic.id))
                  }
                  className="text-left p-4 rounded-xl border border-slate-200 dark:border-slate-700 hover:bg-indigo-50 hover:border-indigo-200 dark:hover:bg-indigo-900/20 dark:hover:border-indigo-700 transition"
                >
                  <h3 className="font-semibold text-slate-900 dark:text-white">{topic.title}</h3>
                  {topic.description && (
                    <p className="text-sm text-slate-600 dark:text-slate-400 mt-1 line-clamp-2">
                      {topic.description}
                    </p>
                  )}
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <CommunicationCanDoProgressCard />

      {commBadges.length > 0 && (
        <Card className="mt-8 border border-violet-100 bg-gradient-to-r from-violet-50/80 to-purple-50/50 dark:from-violet-950/30 dark:to-purple-950/20">
          <CardHeader>
            <CardTitle className="text-base text-purple-900 dark:text-purple-100">Communication badges</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {commBadges.map((b, i) => (
                <span
                  key={`${b.badge_key}-${i}`}
                  className="text-xs font-medium px-3 py-1 rounded-full bg-white/90 dark:bg-slate-800 border border-purple-200 text-purple-900 dark:text-purple-100"
                >
                  {b.badge_key ?? "badge"}
                </span>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* 6. From your teacher */}
      <CommunicationAssignmentsPanel variant="dashboard" />

      {/* 7. Tamil Tutor activity (practice time, visits, voice feedback) */}
      <TutorAnalyticsDashboard />
    </Layout>
  );
};    